'use client';

import { useEffect, useRef, useState } from 'react';
import { PhoneIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { Button, Modal } from '@components/common';
import { JobFolderSelector } from '@components/templates/cv/partials/JobFolderSelector';
import { OperationJob } from '@utils/data-fetching';
import { useStaticToasts } from '@hooks/useStaticToasts';
import { RecruiterCallSoftphoneModal } from './RecruiterCallSoftphoneModal';
import { useRecruiterCallEntry } from './useRecruiterCallEntry';

interface CallCandidateTalentActionProps {
  candidateName: string;
  /** Slug on the talent-search card DTO — backend echoes back the resolved userId on the quote. */
  candidateSlug: string;
  buttonClassName?: string;
  iconClassName?: string;
}

/**
 * Talent-Search variant of the Recruiter Call entry point. Unlike the
 * applicant/pipeline flow, the talent card has no ambient jobSlug, so
 * clicking Call opens a JobFolderSelector modal first. Once the recruiter
 * picks a job, the quote fetches for that `(candidate, job)` pair and,
 * when it resolves, the softphone modal opens directly (skipping the
 * intermediate hover pattern of the card-footer variant).
 *
 * If the quote comes back as `canCall=false` (rare — job locked, wallet
 * empty, service missing) the picker closes and surfaces an error toast
 * so the recruiter isn't left staring at a spinner.
 */
export const CallCandidateTalentAction = ({
  candidateName,
  candidateSlug,
  buttonClassName,
  iconClassName,
}: CallCandidateTalentActionProps) => {
  const { t } = useTranslation('common');
  const { statusGenericErrorToast } = useStaticToasts();

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<OperationJob | undefined>();
  // Guards against the JobFolderSelector's built-in behaviour: on a pick,
  // it fires `sendJobFolderInfoToParent(job)` AND then `setSelectedView('default')`
  // back-to-back. Without this flag, the 'default' transition would look
  // like a cancel and wipe the just-set `selectedJob`.
  const justPickedJobRef = useRef(false);

  const {
    visible,
    isLoading,
    priceCredits,
    resolvedCandidateUserId,
    isModalOpen,
    openModal,
    closeModal,
  } = useRecruiterCallEntry({
    candidateSlug,
    jobSlug: selectedJob?.jobSlug ?? null,
    shouldFetch: Boolean(selectedJob?.jobSlug),
  });

  // React to quote resolution: open softphone on canCall, toast on refusal.
  useEffect(() => {
    if (!selectedJob || isLoading) return;

    if (visible && resolvedCandidateUserId !== null && !isModalOpen) {
      openModal();
      setIsPickerOpen(false);
    } else if (!visible) {
      statusGenericErrorToast();
      setIsPickerOpen(false);
      setSelectedJob(undefined);
    }
  }, [selectedJob, isLoading, visible, resolvedCandidateUserId, isModalOpen, openModal, statusGenericErrorToast]);

  const softphoneJobSlug = selectedJob?.jobSlug;

  const resetPicker = () => {
    setIsPickerOpen(false);
    setSelectedJob(undefined);
  };

  return (
    <>
      <Button
        styling="text"
        size="sm"
        color="ink-medium"
        rounding="none"
        className={buttonClassName}
        onClick={() => setIsPickerOpen(true)}
        title={t('talent-card.v2.action.call', { defaultValue: 'Sună' })}
      >
        <PhoneIcon className={iconClassName} />
        <span>{t('talent-card.v2.action.call', { defaultValue: 'Sună' })}</span>
      </Button>

      {isPickerOpen && (
        <Modal
          isOpen
          onClose={resetPicker}
          size="sm"
          className="!overflow-visible"
        >
          <Modal.Header onClose={resetPicker}>
            {t('talent-card.v2.action.call.job.picker.title', { defaultValue: 'Pentru care job vrei să suni?' })}
          </Modal.Header>
          <Modal.Body>
            {selectedJob ? (
              // Once a job is picked, hide the selector so the
              // JobFolderSelector's automatic transition back to its
              // 'default' view (Edit + Add buttons) doesn't flash while
              // the quote fetches. Softphone opens as soon as the
              // quote resolves.
              <p className="text-sm text-ink-medium">
                {t('talent-card.v2.action.call.job.picker.loading', { defaultValue: 'Verificăm costul apelului…' })}
              </p>
            ) : (
              <JobFolderSelector
                sendJobFolderInfoToParent={(job) => {
                  // Guard against the JobFolderSelector "add-new-job"
                  // placeholder ({jobId: 0, jobSlug: ''}) — with
                  // initialView='select-job' the Add path shouldn't be
                  // reachable, but keep the guard so a truly-empty slug
                  // never becomes a live call target.
                  if (job?.jobSlug) {
                    justPickedJobRef.current = true;
                    setSelectedJob(job);
                  }
                }}
                initialView="select-job"
                onChangeView={(view) => {
                  // JobFolderSelector transitions to 'default' both when
                  // the user cancels (X button) AND right after a
                  // successful pick. `justPickedJobRef` distinguishes
                  // the two — cancel = close picker, pick = keep picker
                  // open until the softphone opens.
                  if (view !== 'default') {
                    return;
                  }
                  if (justPickedJobRef.current) {
                    justPickedJobRef.current = false;
                    return;
                  }
                  resetPicker();
                }}
              />
            )}
          </Modal.Body>
        </Modal>
      )}

      {isModalOpen && resolvedCandidateUserId !== null && softphoneJobSlug && (
        <RecruiterCallSoftphoneModal
          open={isModalOpen}
          candidateName={candidateName}
          candidateUserId={resolvedCandidateUserId}
          jobSlug={softphoneJobSlug}
          priceCredits={priceCredits}
          onClose={() => {
            closeModal();
            setSelectedJob(undefined);
          }}
        />
      )}
    </>
  );
};
